import { Injectable, Inject, PLATFORM_ID, TransferState } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { CHANNEL_TOKEN_LS_KEY, SSR_CHANNEL_TOKEN_KEY } from './channel-token.token';
import { isLocalStorageUsable, safeGetLocalStorage, safeSetLocalStorage } from '../../../common/utils/local-storage-cheker';
import { checkCookie } from '../../../common/utils/cookie-checker';

export type ChannelTokenStatus = 'ok' | 'missing' | 'unsupported';

@Injectable({ providedIn: 'root' })
export class ChannelTokenService {
  private inMemoryToken: string | null = null;
  private lsUsable: boolean | null = null;
  private cookiesEnabled: boolean | null = null;
  private readonly _status$ = new BehaviorSubject<ChannelTokenStatus>('missing');
  readonly status$ = this._status$.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private transfer: TransferState,
  ) {}

  /**
   * Called once via APP_INITIALIZER.
   * - Server: seed TransferState for SSR apollo + hydration.
   * - Browser: read token (prefer provided token, else TransferState), try localStorage as fallback cache.
   *   If we have *some* token source (cookie enabled or token exists), we’re OK.
   *   If neither token nor cookies, treat as missing.
   */
  init(channelTok: string | null): void {
    if (isPlatformServer(this.platformId)) {
      if (channelTok) {
        this.transfer.set(SSR_CHANNEL_TOKEN_KEY, channelTok);
      }
      return;
    }

    this.lsUsable = isLocalStorageUsable();
    this.cookiesEnabled = checkCookie();

    const ssrToken = this.transfer.get<string | null>(SSR_CHANNEL_TOKEN_KEY, null);
    const token = channelTok ?? ssrToken;
    this.inMemoryToken = token;
    this.transfer.remove(SSR_CHANNEL_TOKEN_KEY);

    if (this.cookiesEnabled === false && this.lsUsable === false) {
      this._status$.next('unsupported');
      return;
    }
    if (token && this.lsUsable) {
      safeSetLocalStorage(CHANNEL_TOKEN_LS_KEY, token);
    }
    const persisted = this.lsUsable ? safeGetLocalStorage(CHANNEL_TOKEN_LS_KEY) : null;
    const hasAny = !!persisted || !!this.inMemoryToken || this.cookiesEnabled === true;
    this._status$.next(hasAny ? 'ok' : 'missing');
  }

  getToken(): string | null {
    if (isPlatformServer(this.platformId)) {
      return this.transfer.get<string | null>(SSR_CHANNEL_TOKEN_KEY, null);
    }
    if (!this.lsUsable) {
      return this.inMemoryToken;
    }
    return safeGetLocalStorage(CHANNEL_TOKEN_LS_KEY) ?? this.inMemoryToken;
  }

  getPersistedToken(): string | null {
    if (!this.lsUsable) return null;
    return safeGetLocalStorage(CHANNEL_TOKEN_LS_KEY);
  }

  ensureChannelForCheckout():
    | { ok: true }
    | { ok: false; reason: 'unsupported' | 'missing' } {

    if (this.cookiesEnabled == null || this.lsUsable == null) return { ok: false, reason: 'missing' };
    if (this.cookiesEnabled === false && this.lsUsable === false)  return { ok: false, reason: 'unsupported' };
    if (this.cookiesEnabled) return { ok: true };

    const hasLocalStorage = this.lsUsable ? !!safeGetLocalStorage(CHANNEL_TOKEN_LS_KEY) : false;
    const hasInMemmory = !!this.inMemoryToken;
    if (hasLocalStorage || hasInMemmory) return { ok: true };

    return { ok: false, reason: 'missing' };
  }

  /** For checkout gating */
  getStatusSnapshot(): ChannelTokenStatus {
    return this._status$.value;
  }
}